import React, { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import {
  FiPlus,
  FiSearch,
  FiUsers,
  FiTrash2,
  FiEdit2,
  FiX,
  FiDownload,
  FiLoader,
  FiMoreHorizontal,
  FiUpload,
  FiMail,
  FiArrowLeft,
  FiInfo,
  FiShield,
} from 'react-icons/fi';
import {
  useGetMailingLists,
  useGetMailingList,
  useAddContacts,
  useDeleteContact,
  useUpdateContact,
  useCsvExport,
  useCsvImport,
  useCreateMailingList,
  useAddContactsToMailingList,
  useDeleteMailingList,
} from '../../api/mailing-lists';
import {
  DeleteConfirmationModal,
  MoreOptionsDropdown,
  NewListModal,
  AddEmailsModal,
  EditContactModal,
} from './Modals';
import { EventPoster } from './EventPoster';
import type { MailingList, MailingContact } from './types';
import { parseMailContactsCsvFile } from '../../utils/mailCsv';
import { toast } from '../../utils/toast';
import { isProfileSubscribersList } from '../../utils/mail';

export const ContactListPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'events' | 'custom'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [contactsSearchQuery, setContactsSearchQuery] = useState('');
  const [selectedListUuid, setSelectedListUuid] = useState<string | null>(null);
  const [contactsPage, setContactsPage] = useState(1);

  const [showNewList, setShowNewList] = useState(false);
  const [showAddEmails, setShowAddEmails] = useState(false);
  const [showDeleteContact, setShowDeleteContact] = useState(false);
  const [showDeleteList, setShowDeleteList] = useState(false);
  const [showEditContact, setShowEditContact] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<MailingContact | null>(null);
  const [listToDelete, setListToDelete] = useState<MailingList | null>(null);
  const [selectedContact, setSelectedContact] = useState<MailingContact | null>(null);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // ─── API Queries ───
  const { data: listsData, isLoading: listsLoading } = useGetMailingLists(1, 100);
  const lists: MailingList[] = listsData?.results ?? [];

  const { data: listDetailData, isLoading: detailLoading } = useGetMailingList(
    selectedListUuid ?? undefined,
    {
      page: contactsPage,
      page_size: 20,
      search: contactsSearchQuery || undefined,
    },
  );

  const selectedListObj = selectedListUuid
    ? (lists.find((l) => l.uuid === selectedListUuid) ?? listDetailData?.data?.mailing_list)
    : null;

  const contacts: MailingContact[] = listDetailData?.data?.contacts?.results ?? [];
  const contactsCount = listDetailData?.data?.contacts?.count ?? 0;
  const contactsNext = listDetailData?.data?.contacts?.next;
  const contactsPrev = listDetailData?.data?.contacts?.previous;

  // ─── API Mutations ───
  const addContactsMutation = useAddContacts(selectedListUuid ?? undefined);
  const deleteContactMutation = useDeleteContact();
  const updateContactMutation = useUpdateContact();
  const csvImportMutation = useCsvImport();
  const csvExportMutation = useCsvExport();
  const createListMutation = useCreateMailingList();
  const deleteListMutation = useDeleteMailingList();
  const addContactsToDynamicList = useAddContactsToMailingList();

  // ─── Filter lists by tab and search ───
  const filteredLists = lists.filter((l) => {
    const matchesTab =
      activeTab === 'all' ? true : activeTab === 'events' ? l.is_event_linked : !l.is_event_linked;
    const matchesSearch = l.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  // ─── Counts ───
  const allCount = lists.length;
  const eventsCount = lists.filter((l) => l.is_event_linked).length;
  const customCount = lists.filter((l) => !l.is_event_linked).length;

  // ─── Handlers ───
  const confirmDeleteContact = () => {
    if (contactToDelete && selectedListUuid) {
      deleteContactMutation.mutate(
        { listUuid: selectedListUuid, contactUuid: contactToDelete.uuid },
        {
          onSuccess: () => {
            setShowDeleteContact(false);
            setContactToDelete(null);
          },
        },
      );
    }
  };

  const handleDeleteList = (list: MailingList) => {
    setListToDelete(list);
    setShowDeleteList(true);
  };

  const confirmDeleteList = () => {
    if (listToDelete) {
      deleteListMutation.mutate(listToDelete.uuid, {
        onSuccess: () => {
          if (selectedListUuid === listToDelete.uuid) {
            setSelectedListUuid(null);
          }
          setShowDeleteList(false);
          setListToDelete(null);
        },
      });
    }
  };

  const handleCreateList = (name: string, emails: string[], eventId?: number) => {
    createListMutation.mutate(
      { name, event_id: eventId },
      {
        onSuccess: (response) => {
          setShowNewList(false);
          const newListUuid = response.data?.uuid;
          if (newListUuid && emails.length > 0) {
            addContactsToDynamicList.mutate(
              {
                listUuid: newListUuid,
                payload: { contacts: emails.map((email) => ({ email })) },
              },
              {
                onSuccess: () => {
                  setSelectedListUuid(newListUuid);
                },
              },
            );
          } else if (newListUuid) {
            setSelectedListUuid(newListUuid);
          }
        },
      },
    );
  };

  const handleAddEmails = (emailsList: string[]) => {
    if (!selectedListUuid) return;
    addContactsMutation.mutate(
      { contacts: emailsList.map((email) => ({ email })) },
      {
        onSuccess: () => setShowAddEmails(false),
      },
    );
  };

  const handleSaveContact = (updated: { email: string; name?: string }) => {
    if (!selectedContact || !selectedListUuid) return;
    updateContactMutation.mutate(
      {
        listUuid: selectedListUuid,
        contactUuid: selectedContact.uuid,
        payload: { email: updated.email, name: updated.name },
      },
      {
        onSuccess: () => {
          setShowEditContact(false);
          setSelectedContact(null);
        },
      },
    );
  };

  const handleExportCSV = () => {
    if (!selectedListUuid) return;
    if ((selectedListObj?.total_contacts ?? 0) === 0) {
      toast.warn('No contacts to export.');
      return;
    }
    csvExportMutation.mutate({
      listUuid: selectedListUuid,
      listName: selectedListObj?.name,
      filters: {
        search: contactsSearchQuery || undefined,
      },
    });
  };

  const getInitials = (contact: MailingContact) => {
    if (contact.name) {
      const parts = contact.name.split(' ');
      return (
        (parts[0]?.[0] ?? '' + (parts[1]?.[0] ?? '')).toUpperCase() ||
        contact.email.substring(0, 2).toUpperCase()
      );
    }
    return contact.email.substring(0, 2).toUpperCase();
  };

  return (
    <>
      <Helmet>
        <title>Contacts | Mail | Musicade</title>
      </Helmet>

      <div className="flex-1 flex flex-col md:flex-row gap-2 min-h-0">
        {/* Middle Column (Lists Pane) */}
        <div
          className={`flex-1 flex flex-col rounded-none md:rounded-[16px] border-0 md:border border-[#262626] bg-[#0E0E0E] min-w-0 overflow-hidden ${
            selectedListUuid ? 'hidden md:flex' : 'flex'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-[#262626] flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <Link
                to="/mail"
                className="md:hidden p-1.5 rounded-lg hover:bg-white/5 text-[#A3A3A3] hover:text-white transition-colors flex-shrink-0"
              >
                <FiArrowLeft size={18} />
              </Link>
              <h2 className="text-base md:text-lg font-semibold text-white truncate">
                <span className="md:hidden">Contacts</span>
                <span className="hidden md:inline">Manage Contact List</span>
              </h2>
            </div>
            <button
              onClick={() => setShowNewList(true)}
              className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-white text-black text-xs md:text-sm font-medium hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              <FiPlus size={14} className="md:hidden" />
              <FiPlus size={16} className="hidden md:block" />
              New List
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1.5 md:gap-2 px-4 md:px-6 py-2.5 md:py-4 flex-shrink-0">
            <TabButton
              label="All"
              count={allCount}
              isActive={activeTab === 'all'}
              onClick={() => setActiveTab('all')}
            />
            <TabButton
              label="Events"
              count={eventsCount}
              isActive={activeTab === 'events'}
              onClick={() => setActiveTab('events')}
            />
            <TabButton
              label="Custom"
              count={customCount}
              isActive={activeTab === 'custom'}
              onClick={() => setActiveTab('custom')}
            />
          </div>

          {/* Search */}
          <div className="px-4 md:px-6 pb-3 md:pb-4 flex-shrink-0">
            <div className="relative">
              <FiSearch
                size={16}
                className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 text-[#737373]"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search lists..."
                className="w-full h-10 md:h-12 pl-10 md:pl-12 pr-3 md:pr-4 rounded-lg md:rounded-xl bg-[#0D0D0D] border border-[#262626] text-xs md:text-sm text-white placeholder-[#737373] focus:outline-none focus:border-[#333] transition-colors"
              />
            </div>
          </div>

          {/* Lists */}
          <div className="flex-1 overflow-y-auto px-3 md:px-6 pb-3 md:pb-4">
            {listsLoading && (
              <div className="flex items-center justify-center py-12">
                <p className="text-sm text-[#737373]">Loading lists...</p>
              </div>
            )}

            {!listsLoading && filteredLists.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 md:py-16 text-center px-6">
                <div className="relative mb-5 flex-shrink-0">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-b from-[#18181b] to-[#09090b] border border-[#27272a] flex items-center justify-center shadow-xl">
                    <FiUsers size={24} className="text-[#525252]" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#2F80ED]/20 border border-[#2F80ED]/30 flex items-center justify-center">
                    <FiPlus size={10} className="text-[#2F80ED]" />
                  </div>
                </div>
                <p className="text-sm font-medium text-white mb-1.5">
                  {searchQuery ? 'No lists found' : 'No lists yet'}
                </p>
                <p className="text-xs text-[#737373] leading-relaxed max-w-[200px]">
                  {searchQuery
                    ? 'Try a different search term'
                    : 'Create your first mailing list to get started'}
                </p>
              </div>
            )}

            <div className="space-y-1">
              {filteredLists.map((list) => (
                <ListRow
                  key={list.uuid}
                  list={list}
                  isSelected={selectedListUuid === list.uuid}
                  onSelect={() => {
                    setSelectedListUuid(list.uuid);
                    setContactsPage(1);
                    setContactsSearchQuery('');
                  }}
                  onDelete={() => handleDeleteList(list)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right Column (List Details Pane) */}
        {selectedListObj ? (
          (() => {
            const isSelectedSystemList = isProfileSubscribersList(selectedListObj);
            return (
              <div
                className={`w-full md:w-[380px] md:max-w-[380px] md:min-w-[300px] flex-shrink-0 flex flex-col flex-1 md:flex-none rounded-none md:rounded-[16px] border-0 md:border border-[#262626] bg-[#0E0E0E] overflow-hidden ${
                  selectedListUuid ? 'flex' : 'hidden md:flex'
                }`}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border-b border-[#262626] flex-shrink-0">
                  <div className="min-w-0 flex-1 flex items-center gap-2">
                    <button
                      onClick={() => setSelectedListUuid(null)}
                      className="md:hidden p-1.5 rounded-lg hover:bg-white/5 text-[#A3A3A3] hover:text-white transition-colors flex-shrink-0"
                    >
                      <FiArrowLeft size={18} />
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3
                          className="text-base font-semibold text-white truncate pr-2"
                          title={selectedListObj.name}
                        >
                          {selectedListObj.name}
                        </h3>
                        {isSelectedSystemList && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-400 bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded-full flex-shrink-0">
                            <FiShield size={10} />
                            System List
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#737373] mt-0.5">
                        {selectedListObj.active_contacts.toLocaleString()}{' '}
                        {selectedListObj.is_event_linked ? 'attendees' : 'contacts'}
                        {selectedListObj.unsubscribed_count > 0 && (
                          <span className="text-red-400/70 ml-1">
                            · {selectedListObj.unsubscribed_count} unsubscribed
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedListUuid(null)}
                    className="hidden md:block p-1.5 rounded-lg hover:bg-white/5 transition-colors flex-shrink-0"
                  >
                    <FiX size={18} className="text-[#737373] hover:text-white" />
                  </button>
                </div>

                {isSelectedSystemList && (
                  <div className="mx-4 md:mx-5 my-2.5 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300 flex items-center gap-2 flex-shrink-0">
                    <FiInfo size={16} className="flex-shrink-0 text-blue-400" />
                    <span>
                      Contacts are managed automatically based on profile subscription actions.
                    </span>
                  </div>
                )}

                {/* Contacts Search and Add */}
                <div className="px-4 md:px-5 py-2.5 md:py-3 space-y-2 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <FiSearch
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]"
                      />
                      <input
                        type="text"
                        value={contactsSearchQuery}
                        onChange={(e) => {
                          setContactsSearchQuery(e.target.value);
                          setContactsPage(1);
                        }}
                        placeholder="Search contacts..."
                        className="w-full h-9 pl-9 pr-3 rounded-lg bg-[#0D0D0D] border border-[#262626] text-xs text-white placeholder-[#737373] focus:outline-none focus:border-[#333] transition-colors"
                      />
                    </div>
                    {!isSelectedSystemList && (
                      <button
                        onClick={() => setShowAddEmails(true)}
                        className="flex items-center gap-1.5 h-9 px-4 rounded-full bg-white text-black text-xs font-semibold hover:bg-gray-100 transition-colors flex-shrink-0 cursor-pointer"
                      >
                        <FiPlus size={12} />
                        Add
                      </button>
                    )}
                  </div>
                  {!isSelectedSystemList && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-1 h-8 px-3 rounded-lg border border-[#262626] text-[10px] text-[#A3A3A3] hover:text-white hover:border-[#333] transition-colors"
                      >
                        <FiUpload size={10} />
                        Import
                      </button>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file && selectedListUuid) {
                            const parseResult = await parseMailContactsCsvFile(file);

                            if (parseResult.errors.length > 0) {
                              toast.error(parseResult.errors[0]);
                              e.target.value = '';
                              return;
                            }

                            if (parseResult.summary.validRows === 0) {
                              toast.error('No valid contacts found in CSV.');
                              e.target.value = '';
                              return;
                            }

                            if (parseResult.summary.skippedRows > 0) {
                              toast.warn(
                                `Importing ${parseResult.summary.validRows} contacts and skipping ${parseResult.summary.skippedRows} invalid rows.`,
                              );
                            }

                            csvImportMutation.mutate({ listUuid: selectedListUuid, file });
                          }
                          e.target.value = '';
                        }}
                        accept=".csv"
                        className="hidden"
                      />
                      <a
                        href="/mail_contacts_sample.csv"
                        download
                        className="text-[10px] text-[#737373] hover:text-white transition-colors"
                      >
                        Sample CSV
                      </a>
                    </div>
                  )}
                </div>

                {/* Contacts List */}
                <div className="flex-1 overflow-y-auto px-3 md:px-5 pb-3 md:pb-4 space-y-1 md:space-y-1.5">
                  {detailLoading ? (
                    <div className="text-center py-12">
                      <p className="text-xs text-[#737373]">Loading contacts...</p>
                    </div>
                  ) : contacts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center mb-3">
                        <FiMail size={16} className="text-[#525252]" />
                      </div>
                      <p className="text-xs text-[#737373]">
                        {contactsSearchQuery
                          ? 'No contacts matching your search'
                          : 'No contacts in this list yet'}
                      </p>
                    </div>
                  ) : (
                    contacts.map((contact) => (
                      <div
                        key={contact.uuid}
                        className={`flex items-center justify-between p-2.5 md:p-3 rounded-lg md:rounded-xl border transition-all duration-150 group ${
                          contact.is_unsubscribed
                            ? 'border-red-900/30 bg-red-950/10 opacity-60'
                            : 'border-[#262626] bg-[#0E0E0E] hover:border-[#333333] hover:bg-[#111]'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 md:gap-3 min-w-0 flex-1">
                          <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-[#1c1c1e] flex items-center justify-center text-[10px] font-bold text-white uppercase flex-shrink-0 border border-[#262626]">
                            {getInitials(contact)}
                          </div>
                          <div className="min-w-0 flex-1">
                            {contact.name && (
                              <p className="text-xs font-semibold text-white truncate">
                                {contact.name}
                              </p>
                            )}
                            <div className="flex items-center gap-1.5">
                              <p className="text-[10px] md:text-[11px] text-[#A3A3A3] truncate font-mono">
                                {contact.email}
                              </p>
                              {contact.is_unsubscribed && (
                                <span className="text-[9px] font-medium text-red-400/80 bg-red-500/10 px-1.5 py-0.5 rounded-full uppercase tracking-wider flex-shrink-0">
                                  Unsubscribed
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {!isSelectedSystemList && (
                          <div className="flex items-center gap-0.5 md:gap-1 flex-shrink-0 opacity-100 md:opacity-40 md:group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => {
                                setSelectedContact(contact);
                                setShowEditContact(true);
                              }}
                              className="p-1.5 md:p-1.5 rounded-lg text-[#737373] hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
                            >
                              <FiEdit2 size={12} />
                            </button>
                            <button
                              onClick={() => {
                                setContactToDelete(contact);
                                setShowDeleteContact(true);
                              }}
                              className="p-1.5 md:p-1.5 rounded-lg text-[#737373] hover:text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                            >
                              <FiTrash2 size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* Pagination */}
                {contactsCount > 20 && (
                  <div className="flex items-center justify-center gap-4 py-2 border-t border-[#262626] flex-shrink-0">
                    <button
                      disabled={!contactsPrev}
                      onClick={() => setContactsPage((p) => Math.max(1, p - 1))}
                      className="px-3 py-1.5 text-[10px] text-[#A3A3A3] hover:text-white disabled:opacity-30 transition-colors"
                    >
                      Previous
                    </button>
                    <span className="text-[10px] text-[#737373]">Page {contactsPage}</span>
                    <button
                      disabled={!contactsNext}
                      onClick={() => setContactsPage((p) => p + 1)}
                      className="px-3 py-1.5 text-[10px] text-[#A3A3A3] hover:text-white disabled:opacity-30 transition-colors"
                    >
                      Next
                    </button>
                  </div>
                )}

                {/* Footer */}
                <div className="p-3 border-t border-[#262626] flex items-center gap-2 bg-[#0E0E0E] flex-shrink-0">
                  <button
                    onClick={handleExportCSV}
                    disabled={
                      csvExportMutation.isPending || (selectedListObj?.total_contacts ?? 0) === 0
                    }
                    className="flex-1 flex items-center justify-center gap-2 h-10 rounded-full border border-[#333] text-white text-xs font-medium hover:bg-white/5 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {csvExportMutation.isPending ? (
                      <FiLoader size={13} className="animate-spin" />
                    ) : (
                      <FiDownload size={13} />
                    )}
                    Export CSV
                  </button>
                </div>
              </div>
            );
          })()
        ) : (
          /* Empty state when no list selected — hidden on mobile */
          <div className="hidden md:flex w-[380px] max-w-[380px] min-w-[300px] flex-shrink-0 flex flex-col items-center justify-center rounded-[16px] border border-[#262626] bg-[#0E0E0E] text-center px-8">
            <div className="w-14 h-14 rounded-full bg-[#1a1a1a] flex items-center justify-center mb-5 border border-[#262626]">
              <FiUsers size={22} className="text-[#525252]" />
            </div>
            <p className="text-base font-semibold text-white mb-2">Select a list</p>
            <p className="text-sm text-[#737373] leading-relaxed max-w-[240px]">
              Choose a mailing list from the left to view and manage its contacts.
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showNewList && (
        <NewListModal onClose={() => setShowNewList(false)} onCreate={handleCreateList} />
      )}
      {showAddEmails && (
        <AddEmailsModal
          onClose={() => setShowAddEmails(false)}
          onAdd={handleAddEmails}
          listName={selectedListObj?.name || 'List'}
        />
      )}
      {showDeleteContact && contactToDelete && (
        <DeleteConfirmationModal
          title="Remove Contact"
          message={`Are you sure you want to remove ${contactToDelete.name || contactToDelete.email} from this list?`}
          onClose={() => {
            setShowDeleteContact(false);
            setContactToDelete(null);
          }}
          onConfirm={confirmDeleteContact}
        />
      )}
      {showDeleteList && listToDelete && (
        <DeleteConfirmationModal
          title="Delete Contact List"
          message={`Are you sure you want to delete "${listToDelete.name}"? All contacts in this list will be removed. This action cannot be undone.`}
          onClose={() => {
            setShowDeleteList(false);
            setListToDelete(null);
          }}
          onConfirm={confirmDeleteList}
        />
      )}
      {showEditContact && selectedContact && (
        <EditContactModal
          contact={{
            email: selectedContact.email,
            name: selectedContact.name,
          }}
          isSaving={updateContactMutation.isPending}
          onClose={() => {
            setShowEditContact(false);
            setSelectedContact(null);
          }}
          onSave={handleSaveContact}
        />
      )}
    </>
  );
};

/* ─── Tab Button ─── */
const TabButton = ({
  label,
  count,
  isActive,
  onClick,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-200 cursor-pointer ${
      isActive ? 'bg-white text-black' : 'bg-[#171717] text-white hover:bg-[#222222]'
    }`}
  >
    {label}
    <span
      className={`text-[10px] md:text-xs tabular-nums ${isActive ? 'text-black/50' : 'text-[#737373]'}`}
    >
      {count.toLocaleString()}
    </span>
  </button>
);

/* ─── List Row ─── */
const ListRow: React.FC<{
  list: MailingList;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}> = ({ list, isSelected, onSelect, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);
  const isSystemList = isProfileSubscribersList(list);

  const getSubtitle = () => {
    if (list.is_event_linked && list.event_title) {
      return list.event_title;
    }
    return `Created ${new Date(list.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  return (
    <div className="relative">
      <div
        onClick={onSelect}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onSelect();
        }}
        className={`w-full flex items-center justify-between px-3 md:px-4 py-2.5 md:py-3.5 rounded-lg md:rounded-xl transition-all duration-150 text-left cursor-pointer ${
          isSelected ? 'bg-white/10' : 'hover:bg-white/5'
        }`}
      >
        <div className="flex items-center gap-3 md:gap-3.5 min-w-0 flex-1">
          {list.is_event_linked ? (
            <EventPoster name={list.event_title || list.name} />
          ) : (
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center flex-shrink-0 border border-[#333]/30">
              <FiUsers size={14} className="md:hidden text-[#A3A3A3]" />
              <FiUsers size={16} className="hidden md:block text-[#A3A3A3]" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium text-white truncate">{list.name}</p>
              {isSystemList && (
                <span className="inline-flex items-center gap-1 text-[9px] font-medium text-blue-400 bg-blue-500/10 border border-blue-500/20 px-1.5 py-0.5 rounded-full flex-shrink-0">
                  <FiShield size={9} />
                  System
                </span>
              )}
            </div>
            <p className="text-xs text-[#737373] truncate mt-0.5">{getSubtitle()}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 md:gap-2.5 flex-shrink-0 ml-2">
          <span className="text-[11px] md:text-xs text-[#737373] tabular-nums whitespace-nowrap">
            {list.total_contacts.toLocaleString()}
            <span className="hidden md:inline">
              {' '}
              {list.is_event_linked ? 'attendees' : 'contacts'}
            </span>
          </span>
          {!isSystemList && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="p-1.5 md:p-2 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
            >
              <FiMoreHorizontal size={14} className="md:hidden text-[#737373]" />
              <FiMoreHorizontal size={16} className="hidden md:block text-[#737373]" />
            </button>
          )}
        </div>
      </div>
      {!isSystemList && showMenu && (
        <div className="absolute right-4 top-12 md:top-14 z-30">
          <MoreOptionsDropdown
            options={[{ label: 'Delete', icon: FiTrash2, danger: true, onClick: onDelete }]}
            onClose={() => setShowMenu(false)}
          />
        </div>
      )}
    </div>
  );
};
